"use client";

import React, { useState } from "react";
import { Target } from "lucide-react";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { GameCard } from "./GameCard";
import { ChallengeZoneModal } from "./ChallengeZoneModal";
import { useChallengeZoneGames } from "./useChallengeZoneGames";
import type { ChallengeZoneProps } from "./types";
import type { ChallengeZoneGame } from "./types";
import { Surface } from "@/components/ui/Surface";

/**
 * Challenge Zone — Sidebar variant.
 * Renders a vertical stack of game cards for sidebar placement.
 * Returns null if no team entities or no games found.
 */
export function ChallengeZoneSidebar({ relatedEntities, locale }: ChallengeZoneProps) {
    const labels = useLabels();
    const { gamification } = useTenantSettings();
    const { games, isLoading } = useChallengeZoneGames(relatedEntities, locale);
    const [selectedGame, setSelectedGame] = useState<ChallengeZoneGame | null>(null);

    if (!gamification.enabled) return null;

    if (!isLoading && games.length === 0) return null;

    const czLabels = labels.challengeZone;

    return (
        <>
            <Surface slot="challengeZone" className="relative overflow-hidden">
                {/* Animated gradient top bar */}
                <div className="h-0.5 bg-primary" />

                <div className="p-4">
                    {/* Header */}
                    <div className="flex items-center gap-2.5 mb-3.5">
                        <div className="bg-gradient-to-br from-primary to-accent w-[30px] h-[30px] rounded-lg flex items-center justify-center shrink-0">
                            <Target className="w-3.5 h-3.5 text-primary-foreground" />
                        </div>
                        <div>
                            <h3 className="font-bold text-sm">{czLabels.title}</h3>
                            <p className="text-[10px] opacity-70">
                                {czLabels.gamesAvailable.replace("{count}", String(games.length))}
                            </p>
                        </div>
                    </div>

                    {isLoading ? (
                        <ChallengeZoneSidebarSkeleton />
                    ) : (
                        <div className="space-y-2.5">
                            {games.map((game) => (
                                <GameCard
                                    key={game.id}
                                    game={game}
                                    labels={czLabels}
                                    onClick={() => setSelectedGame(game)}
                                    compact
                                />
                            ))}
                        </div>
                    )}
                </div>
            </Surface>

            <ChallengeZoneModal game={selectedGame} onClose={() => setSelectedGame(null)} />
        </>
    );
}

function ChallengeZoneSidebarSkeleton() {
    return (
        <div className="space-y-2.5">
            {[0, 1, 2].map((i) => (
                <div
                    key={i}
                    className="h-[88px] rounded-xl bg-foreground/[0.03] animate-pulse"
                    style={{ animationDelay: `${i * 200}ms` }}
                />
            ))}
        </div>
    );
}
